import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { conversationsAPI, messagesAPI } from '../../services/api';

export interface Conversation {
  id: number;
  display_id: number;
  status: 'open' | 'pending' | 'resolved' | 'snoozed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  contact_id: number;
  channel_id: number;
  account_id: number;
  assignee_id?: number;
  team_id?: number;
  created_at: string;
  updated_at: string;
  last_activity_at?: string;
  contact?: {
    id: number;
    name: string;
    email?: string;
    phone_number?: string;
    avatar_url?: string;
  };
  assignee?: {
    id: number;
    name: string;
    avatar_url?: string;
  };
  channel?: {
    id: number;
    name: string;
    channel_type: string;
  };
  labels?: Array<{
    id: number;
    title: string;
    color: string;
  }>;
  unread_count?: number;
}

export interface Message {
  id: number;
  content: string;
  message_type: 'incoming' | 'outgoing' | 'activity';
  content_type: 'text' | 'image' | 'file' | 'audio' | 'video';
  conversation_id: number;
  sender_id?: number;
  contact_id?: number;
  status: string;
  private: boolean;
  created_at: string;
  sender?: {
    id: number;
    name: string;
    avatar_url?: string;
  };
  attachments?: Array<{
    id: number;
    file_type: string;
    file_url: string;
    file_size?: number;
  }>;
}

interface ConversationsState {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  messagesLoading: boolean;
  error: string | null;
  filters: {
    status: string;
    assignee: string;
    team: string;
    search: string;
  };
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

const initialState: ConversationsState = {
  conversations: [],
  selectedConversation: null,
  messages: [],
  loading: false,
  messagesLoading: false,
  error: null,
  filters: {
    status: 'all',
    assignee: 'all',
    team: 'all',
    search: '',
  },
  pagination: {
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  },
};

// Async thunks
export const fetchConversations = createAsyncThunk(
  'conversations/fetchConversations',
  async (params?: any) => {
    const response = await conversationsAPI.getConversations(params);
    return response;
  }
);

export const fetchConversation = createAsyncThunk(
  'conversations/fetchConversation',
  async (id: number) => {
    const response = await conversationsAPI.getConversation(id);
    return response;
  }
);

export const fetchMessages = createAsyncThunk(
  'conversations/fetchMessages',
  async ({ conversationId, params }: { conversationId: number; params?: any }) => {
    const response = await messagesAPI.getMessages(conversationId, params);
    return response;
  }
);

export const sendMessage = createAsyncThunk(
  'conversations/sendMessage',
  async ({ conversationId, data }: { conversationId: number; data: any }) => {
    const response = await messagesAPI.sendMessage(conversationId, data);
    return response;
  }
);

export const updateConversation = createAsyncThunk(
  'conversations/updateConversation',
  async ({ id, data }: { id: number; data: any }) => {
    const response = await conversationsAPI.updateConversation(id, data);
    return response;
  }
);

export const assignConversation = createAsyncThunk(
  'conversations/assignConversation',
  async ({ id, assigneeId, teamId }: { id: number; assigneeId?: number; teamId?: number }) => {
    await conversationsAPI.assignConversation(id, assigneeId, teamId);
    return { id, assigneeId, teamId };
  }
);

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setSelectedConversation: (state, action: PayloadAction<Conversation | null>) => {
      state.selectedConversation = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<typeof initialState.filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
    },
    updateMessage: (state, action: PayloadAction<{ id: number; updates: Partial<Message> }>) => {
      const index = state.messages.findIndex(msg => msg.id === action.payload.id);
      if (index !== -1) {
        state.messages[index] = { ...state.messages[index], ...action.payload.updates };
      }
    },
    removeMessage: (state, action: PayloadAction<number>) => {
      state.messages = state.messages.filter(msg => msg.id !== action.payload);
    },
    updateConversationInList: (state, action: PayloadAction<Partial<Conversation> & { id: number }>) => {
      const index = state.conversations.findIndex(conv => conv.id === action.payload.id);
      if (index !== -1) {
        state.conversations[index] = { ...state.conversations[index], ...action.payload };
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch conversations
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.conversations || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch conversations';
      })
      
      // Fetch conversation
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.selectedConversation = action.payload;
        // Update in list if exists
        const index = state.conversations.findIndex(conv => conv.id === action.payload.id);
        if (index !== -1) {
          state.conversations[index] = action.payload;
        }
      })
      
      // Fetch messages
      .addCase(fetchMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        state.messages = action.payload.messages || action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.messagesLoading = false;
        state.error = action.error.message || 'Failed to fetch messages';
      })
      
      // Send message
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.messages.push(action.payload);
      })
      
      // Update conversation
      .addCase(updateConversation.fulfilled, (state, action) => {
        const index = state.conversations.findIndex(conv => conv.id === action.payload.id);
        if (index !== -1) {
          state.conversations[index] = action.payload;
        }
        if (state.selectedConversation?.id === action.payload.id) {
          state.selectedConversation = action.payload;
        }
      })
      
      // Assign conversation
      .addCase(assignConversation.fulfilled, (state, action) => {
        const { id, assigneeId, teamId } = action.payload;
        const index = state.conversations.findIndex(conv => conv.id === id);
        if (index !== -1) {
          state.conversations[index].assignee_id = assigneeId;
          state.conversations[index].team_id = teamId;
        }
        if (state.selectedConversation?.id === id) {
          state.selectedConversation.assignee_id = assigneeId;
          state.selectedConversation.team_id = teamId;
        }
      });
  },
});

export const {
  setSelectedConversation,
  setFilters,
  clearMessages,
  addMessage,
  updateMessage,
  removeMessage,
  updateConversationInList,
  clearError,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;